"use client"

import Breadcrumbs from "@/components/Breadcrumbs"
import FormError from "@/components/FormError"
import Price from "@/components/Price"
import RichContent from "@/components/RichContent/richContent"
import { Form } from "@/components/ui/form"
import { mittInstance } from "@/constants/mittInstance"
import { SIZE_CHART_SKU } from "@/constants/size-chart-sku"
import { formatString } from "@/lib/format-string"
import { getNewUrlKey } from "@/lib/get-product-full-detail-seo"
import { cn } from "@/lib/utils"
import {
  CategoryInterface,
  ConfigurableProduct,
  ConfigurableProductOptions,
  Maybe,
} from "@/types/graphql.prod"
import { findMatchingVariant } from "@/util/findMatchingProductVariant"
import { isProductConfigurable } from "@/util/isProductConfigurable"
import { isSupportedProductType as isSupported } from "@/util/isSupportedProductType"
import { ratingToStarValue } from "@/util/rating-to-star-value"
import { zodResolver } from "@hookform/resolvers/zod"
import Image from "next/image"
import { useSearchParams } from "next/navigation"
import { useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useInView } from "react-intersection-observer"
import { useIntl } from "react-intl"
import { z } from "zod"
import { useMiniCartStore } from "../MiniCart/use-new-minicart-store"
import { Button } from "../ui/button"
import { NewNotifyMeFormDialog } from "./NotifyMeFormDialog/new-notify-me-form-dialog"
import { AddCartButton } from "./add-cart-button"
import AddToCartLoadingIcon from "./add-to-cart-loading-icon"
import { ProductDetailAndShipping } from "./product-detail-and-shipping"
import { QuantitySelect } from "./quantity-select"
import ShareLinkDialog from "./share-link-dialog"
import { StandardOptions } from "./standard-options"
import { useAddToCart } from "./use-add-to-cart"
import { useGetIsOutOfStock } from "./use-get-is-out-of-stock"
import { useGetOptionCode } from "./use-get-option-code"
import { useGetProductPrice } from "./use-get-product-price"
import { useOptionsSpecs } from "./use-options-sepcs"
import { useGetMediaGalleryEntries } from "./useGetMediaGalleryEntries"
import TTBF001CP from "./TTBF001CP"
import TTBF001CPParams from "./TTBF001CPParams"
import TTBF002CP from "./TTBF002CP"
import TTBF002CPParams from "./TTBF002CPParams"
import Rate from "@/components/Rate"
import classes from "./productFullDetail.module.css"
import ProductImageCarousel from "../product-image-carousel"
import { useIsMobile } from "@/hooks/use-is-mobile"
import { ProductCarousel } from "./product-carousel"
import { Portal } from "@/components/Portal"
import { useAppContext } from "@/context/app"
export const calculateDeliveryDate = (start: number, end: number) => {
  const currentDate = new Date()
  const startDate = new Date(currentDate)

  startDate.setDate(currentDate.getDate() + start)
  const endDate = new Date(currentDate)

  endDate.setDate(currentDate.getDate() + end)

  const formatDate = (date: Date) => {
    const month = date.toLocaleString("en-US", {
      month: "short",
    })
    const day = date.getDate()

    // Changed format to include period after month abbreviation
    return `${month}. ${day}`
  }

  return `${formatDate(startDate)} - ${formatDate(endDate)}`
}

export interface SelectedConfigurableOption {
  uid: string
  label: string
  option_attribute_id: string
  option_selection_value: number
}

export interface SelectedOptions {
  [key: string]: SelectedConfigurableOption | number | string
  quantity: number | string
}

export interface SelectedOptionsWithoutQuantity {
  [key: string]: SelectedConfigurableOption
}

// Correlate a GQL error message to a field. GQL could return a longer error
// string but it may contain contextual info such as product id. We can use
// parts of the string to check for which field to apply the error.
const ERROR_MESSAGE_TO_FIELD_MAPPING: { [key: string]: string } = {
  "The requested qty is not available": "quantity",
  "Product that you are trying to add is not available.": "quantity",
  "The product that was requested doesn't exist.": "quantity",
}

// Field level error messages for rendering.
const ERROR_FIELD_TO_MESSAGE_MAPPING: { [key: string]: string } = {
  quantity: "The requested quantity is not available.",
}

const getBreadcrumbCategoryId = (
  categories: Maybe<Maybe<CategoryInterface>[]> | undefined
) => {
  // Exit if there are no categories for this product.
  if (!categories || !categories.length) {
    return
  }

  const breadcrumbSet: Set<any> = new Set()

  categories?.forEach((category) => {
    const breadcrumbs = category?.breadcrumbs

    breadcrumbs?.forEach((breadcrumb) => {
      const category_id = breadcrumb?.category_id

      breadcrumbSet.add(category_id)
    })
  })

  // Until we can get the single canonical breadcrumb path to a product we
  // will just return the first category id of the potential leaf categories.
  const leafCategory = categories.find(
    (category) => !breadcrumbSet.has(category?.uid)
  )

  // If we couldn't find a leaf category then just use the first category
  // in the list for this product.
  return leafCategory?.uid || categories?.[0]?.uid
}

export const getIsPreOrderInfo = (
  product: ConfigurableProduct,
  optionCodes: Map<any, any>,
  optionSelections: Map<any, any>
) => {
  const { variants } = product
  const isConfigurable = isProductConfigurable(product)
  const optionsSelected =
    Array.from(optionSelections.values()).filter((value) => !!value).length > 0

  // cp 产品
  if (isConfigurable && optionsSelected) {
    const item = findMatchingVariant({
      optionCodes,
      optionSelections,
      variants,
    })

    const { product } = item || {}

    const { presale_product } = product || {}

    const note = (
      <>
        Order now, receive by:{" "}
        <span className="text-info">
          &nbsp;
          {calculateDeliveryDate(
            presale_product?.shipping_from,
            presale_product?.shipping_to
          )}
        </span>
      </>
    )

    const { status: isPreOrder, label } = presale_product || {}

    if (isPreOrder) {
      return {
        isPreOrder: true,
        note,
        label,
      }
    }

    return {
      isPreOrder: false,
      note: "",
    }
  }

  const { presale_product } = product || {}

  const { status: isPreOrder, note, label } = presale_product || {}

  return {
    isPreOrder,
    note,
    label,
  }
}

export const ProductFullDetail = ({
  product,
  reviewData,
  onViewReviews,
  onViewQuestions,
  defaultValues,
  handleColorLabelChange,
  seoColorLabel,
  hasMoreThanOneConfigurableOptions,
  hadNoColor,
  productDetailMarkId,
}: {
  product: ConfigurableProduct
  reviewData: any
  onViewReviews: any
  onViewQuestions: any
  defaultValues: any
  handleColorLabelChange: any
  seoColorLabel: string
  hasMoreThanOneConfigurableOptions: boolean
  hadNoColor: boolean
  productDetailMarkId: string
}) => {
  const { ref, inView } = useInView({
    threshold: 0,
  })

  console.log({
    product,
  })
  const { isMobile } = useIsMobile()

  const createFormSchema = (
    options: Maybe<Maybe<ConfigurableProductOptions>[]> | undefined
  ) => {
    const schemaFields: Record<string, z.ZodTypeAny> = {}

    schemaFields["quantity"] = z.string().min(1, {
      message: `This is a required field!`,
    })

    if (!options) {
      return z.object(schemaFields)
    }

    options?.forEach((item) => {
      if (item?.attribute_code) {
        schemaFields[item?.attribute_code] = z.object(
          {
            uid: z.string().min(1),
            label: z.string(),
            option_attribute_id: z.string(),
            option_selection_value: z.number().optional(),
          },
          {
            message: `This is a required field!`,
          }
        )
      }
    })

    return z.object(schemaFields)
  }

  const FormSchema = createFormSchema(product?.configurable_options)

  const form = useForm({
    resolver: zodResolver(FormSchema as any),
    defaultValues: defaultValues,
  })
  const allCPData = form.watch() as SelectedOptions
  const allCpDataWithoutQuantity = Object.fromEntries(
    Object.entries(allCPData).filter(([key]) => key !== "quantity")
  ) as SelectedOptionsWithoutQuantity
  const selectedOptionsArray = useMemo(() => {
    return Object.values(allCpDataWithoutQuantity)
      .filter((item) => item && item.uid)
      .map((item) => item.uid)
  }, [allCpDataWithoutQuantity])

  const searchParams = useSearchParams()

  const handleRefreshUrlKey = (
    colorLabel: string,
    packageQuantityLabel?: string,
    hadNoColor?: boolean
  ) => {
    const newUrlKey = getNewUrlKey({
      colorLabel,
      packageQuantityLabel,
      originalUrlKey: product?.url_key ?? "",
      searchParamsString: searchParams.toString(),
      hadNoColor,
    })

    window.history.replaceState(
      {
        color,
      },
      "",
      newUrlKey
    )
  }

  const color = allCpDataWithoutQuantity["color"]?.label?.toLowerCase()
  const package_quantity =
    allCpDataWithoutQuantity["package_quantity"]?.label?.toLowerCase()

  const [showShareLinkModal, setShowShareLinkModal] = useState(false)

  useEffect(() => {
    if (hasMoreThanOneConfigurableOptions) {
      if (color && package_quantity) {
        handleRefreshUrlKey(color, package_quantity)
        //
        console.log("执行 refresh")
      }
    } else if (hadNoColor) {
      handleRefreshUrlKey("", package_quantity, hadNoColor)
    } else {
      if (color) {
        handleRefreshUrlKey(color)
        console.log("执行 refresh")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [package_quantity, color])

  const { optionCodes } = useGetOptionCode({
    product,
  })
  const optionSelections: Map<any, any> = useMemo(() => {
    const optionMap = new Map()
    const options = product?.configurable_options || []

    options?.forEach((item) => {
      const attribute_id = item?.attribute_id

      optionMap.set(attribute_id, undefined)
    })

    // Iterate through each property in the JSON object
    for (const [key, value] of Object.entries(allCpDataWithoutQuantity)) {
      if (typeof value === "object" && value !== null) {
        const item = allCpDataWithoutQuantity[key]

        optionMap.set(item.option_attribute_id, item.option_selection_value)
      }
    }

    return optionMap
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCpDataWithoutQuantity])

  const isPreOrderInfo = useMemo(
    () => getIsPreOrderInfo(product, optionCodes, optionSelections),
    [product, optionCodes, optionSelections]
  )

  const productType = product.__typename
  const isSupportedProductType = isSupported(productType)
  const [notifyMeFormDialogIsOpen, setNotifyMeFormDialogIsOpen] =
    useState(false)
  const breadcrumbCategoryId = useMemo(
    () => getBreadcrumbCategoryId(product.categories),
    [product.categories]
  )
  const { productPrice } = useGetProductPrice({
    product,
    optionCodes,
    optionSelections,
  })
  const { mediaGalleryEntries } = useGetMediaGalleryEntries({
    product,
    optionCodes,
    optionSelections,
  })
  const { isOutOfStock } = useGetIsOutOfStock({
    product,
    optionCodes,
    optionSelections,
  })
  const productDetails = {
    description: product.description,
    shortDescription: product.short_description,
    name: product.name,
    price: productPrice?.final_price,
    sku: product.sku,
    videoUrl: product.video_url,
  }

  const setCartStatus = useMiniCartStore((state) => state?.setCartStatus)
  const [{ headerVisibleHeight }] = useAppContext()

  const {
    handleAddToCart,
    isAddingProduct,
    addToCartStatus,
    addToCartQty,
    errorMessage,
  } = useAddToCart({
    product,
    mediaGalleryEntries,
    selectedOptionsArray,
    afterSubmit: () => {
      setCartStatus("addtocart")
    },
  })

  const { isPreOrder, note, label } = isPreOrderInfo || {}

  const { formatMessage } = useIntl()

  // eslint-disable-next-line no-undef
  const errors = new Map()

  if (errorMessage) {
    Object.keys(ERROR_MESSAGE_TO_FIELD_MAPPING).forEach((key) => {
      if (errorMessage.includes(key)) {
        const target = ERROR_MESSAGE_TO_FIELD_MAPPING[key]
        const message = ERROR_FIELD_TO_MESSAGE_MAPPING[target as any]

        errors.set(target, message)
      }
    })

    // Handle cases where a user token is invalid or expired. Preferably
    // this would be handled elsewhere with an error code and not a string.
    if (errorMessage.includes("The current user cannot")) {
      errors.set("form", [
        new Error(
          formatMessage({
            id: "productFullDetail.errorToken",
            defaultMessage:
              "There was a problem with your cart. Please sign in again and try adding the item once more.",
          })
        ),
      ])
    }

    // Handle cases where a cart wasn't created properly.
    if (errorMessage.includes('Variable "$cartId" got invalid value null')) {
      errors.set("form", [
        new Error(
          formatMessage({
            id: "productFullDetail.errorCart",
            defaultMessage:
              "There was a problem with your cart. Please refresh the page and try adding the item once more.",
          })
        ),
      ])
    }

    // An unknown error should still present a readable message.
    if (!errors.size) {
      errors.set("form", [
        new Error(
          formatMessage({
            id: "productFullDetail.errorUnknown",
            defaultMessage:
              "Could not add item to cart. Please check required options and try again.",
          })
        ),
      ])
    }
  }

  const { optionSpecs, handleClick } = useOptionsSpecs({
    product,
  })

  const onSubmit = (data: any) => {
    const { quantity } = data

    handleAddToCart(quantity)
  }

  const showReviewForm = () => {
    const event = new CustomEvent("SHOW_REVIEW_FORM_EVENT", {
      detail: {
        type: "SHOW_REVIEW_FORM_EVENT",
        payload: {},
      },
    })

    window.dispatchEvent(event)
  }

  const shortDescription = productDetails.shortDescription ? (
    <RichContent html={productDetails.shortDescription.html} />
  ) : null

  const carouselRenderImages = useMemo(() => {
    return Array.isArray(mediaGalleryEntries)
      ? mediaGalleryEntries
          .filter((x) => !!x && !x.disabled)
          .sort((a, b) => (a.position || 0) - (b.position || 0))
      : []
  }, [mediaGalleryEntries])

  const rating =
    reviewData && reviewData.review_count ? (
      <section className="flex items-center">
        <Rate
          disabled
          initialRating={ratingToStarValue(reviewData.rating_summary)}
        />
        <span
          className="ml-[10px] cursor-pointer font-sans text-sm text-title underline hover_text-primary"
          onClick={onViewReviews}
        >
          {reviewData.review_count} Reviews
        </span>
      </section>
    ) : (
      <section className="flex items-center" onClick={showReviewForm}>
        <Rate disabled initialRating={0} />
        <span className="ml-[10px] cursor-pointer font-sans text-sm text-title underline hover_text-primary">
          Be the first to review
        </span>
      </section>
    )

  const questions = product.question_answer?.length ? (
    <span className="flex items-center">
      <span className="ml-[10px] inline-block h-[20px] w-[1px] bg-[#000]"></span>
      <span
        onClick={onViewQuestions}
        className="ml-[10px] cursor-pointer font-sans text-sm text-title underline hover_text-primary"
      >
        {product.question_answer?.length} Questions
      </span>
    </span>
  ) : null

  return (
    <div className="z-1 relative lg_pt-[20px]">
      <ShareLinkDialog
        open={showShareLinkModal}
        setOpen={setShowShareLinkModal}
      />
      <div className="mx-auto mb-4 hidden w-[1440px] max-w-full lg_block">
        {breadcrumbCategoryId ? (
          <Breadcrumbs
            categoryId={breadcrumbCategoryId}
            currentProduct={productDetails.name}
          />
        ) : null}
      </div>
      <div className="mx-auto flex w-[1440px] max-w-full flex-col px-[17px] pt-[20px] lg_flex-row lg_items-start lg_gap-[40px] lg_px-0 lg_pb-[20px] lg_pt-0">
        <div className="mb-[10px] block lg_hidden">
          <h1
            aria-live="polite"
            className="mb-[10px] font-poppins text-[18px] font-[600] leading-[1.4] text-title"
            data-cy="ProductFullDetail-productName"
          >
            {formatString(productDetails?.name ?? "")}
          </h1>
          <div className="flex items-center">
            {rating}
            {questions}
          </div>
        </div>
        <section
          className="w-full lg_sticky lg_w-[830px]"
          style={{ top: isMobile ? undefined : headerVisibleHeight }}
        >
          {isMobile ? (
            <ProductCarousel
              images={carouselRenderImages}
              videoUrl={productDetails?.videoUrl ?? ""}
              seoColorLabel={seoColorLabel}
              name={product.name ?? ""}
            />
          ) : (
            <ProductImageCarousel
              gallery={carouselRenderImages}
              videoUrl={productDetails?.videoUrl ?? ""}
              productName={productDetails.name ?? ""}
            />
          )}
        </section>
        <div className="sticky top-0 z-[2] w-full lg_flex-1">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit, (errors) => {
                // 处理错误，滚动到第一个错误字段
                const errorElement = document.querySelector(`#form-error`)

                if (errorElement) {
                  // 平滑滚动到错误元素
                  errorElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  })
                }
              })}
            >
              <div className="">
                <section className={cn("flex flex-col", "px-0")}>
                  <div className="hidden items-start gap-x-2.5 lg_flex">
                    <h1
                      aria-live="polite"
                      className="mb-[20px] font-poppins text-[20px] font-[600] leading-[1.4] text-title"
                      data-cy="ProductFullDetail-productName"
                    >
                      {formatString(productDetails?.name ?? "")}
                    </h1>
                    <button
                      type="button"
                      className="flex-none lg_hidden"
                      onClick={() => {
                        setShowShareLinkModal(true)
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="25"
                        height="25"
                        fill="none"
                        viewBox="0 0 25 25"
                      >
                        <path
                          fill="#2F6132"
                          d="M20.955 14.375a.875.875 0 0 0-.875.875v2.025a3.18 3.18 0 0 1-3.176 3.176H8.103a3.18 3.18 0 0 1-3.175-3.176V7.632a3.18 3.18 0 0 1 3.175-3.176h1.6a.875.875 0 0 0 0-1.75h-1.6a4.93 4.93 0 0 0-4.925 4.926v9.64A4.93 4.93 0 0 0 8.103 22.2h8.801a4.93 4.93 0 0 0 4.926-4.927V15.25a.875.875 0 0 0-.875-.875"
                        />
                        <path
                          fill="#2F6132"
                          d="M13.28 12.389a.88.88 0 0 0 .625-.266l5.92-6.125V8.84a.874.874 0 1 0 1.75 0V4.966a2.136 2.136 0 0 0-2.133-2.133h-3.58a.875.875 0 1 0 0 1.75h2.902l-6.112 6.325a.875.875 0 0 0 .625 1.482z"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="hidden items-center justify-between lg_flex lg_flex-col lg_items-stretch lg_justify-start">
                    <div className="mb-[20px] flex items-center">
                      {rating}
                      {questions}
                    </div>

                    <div className="font-poppins text-[24px] font-[600] leading-[1.4] text-title">
                      <Price
                        currencyCode={productDetails.price.currency}
                        value={productDetails.price.value}
                      />
                      <span className="font-normal text-paragraph line-through ">
                        {product?.price_range?.maximum_price?.discount
                          ?.amount_off &&
                        product?.price_range?.maximum_price?.discount
                          ?.amount_off > 0 ? (
                          <Price
                            currencyCode={
                              product?.price?.regularPrice?.amount?.currency
                            }
                            value={product?.price?.regularPrice?.amount?.value}
                          />
                        ) : null}
                      </span>
                    </div>
                  </div>

                  <div className="mt-[20px] flex items-center justify-between lg_hidden">
                    <div className="font-poppins text-[24px] font-[600] leading-[1.4] text-title">
                      <Price
                        currencyCode={productDetails.price.currency}
                        value={productDetails.price.value}
                      />
                      <span className="font-normal text-paragraph line-through ">
                        {product?.price_range?.maximum_price?.discount
                          ?.amount_off &&
                        product?.price_range?.maximum_price?.discount
                          ?.amount_off > 0 ? (
                          <Price
                            currencyCode={
                              product?.price?.regularPrice?.amount?.currency
                            }
                            value={product?.price?.regularPrice?.amount?.value}
                          />
                        ) : null}
                      </span>
                    </div>
                    <div
                      className={classes.shareIcon}
                      onClick={() => {
                        setShowShareLinkModal(true)
                      }}
                    ></div>
                  </div>

                  {shortDescription}
                </section>

                <FormError errors={errors.get("form") || []} />
                <section className={"pt-[10px] lg_pt-[40px]"}>
                  <StandardOptions
                    configurableOptions={product.configurable_options}
                    variants={product.variants}
                    product={product}
                    optionSpecs={optionSpecs}
                    handleClick={handleClick}
                    hasSizeChart={
                      !!product.sku && SIZE_CHART_SKU.includes(product.sku)
                    }
                    onShowSizeChart={() => {
                      if (product.sku) {
                        mittInstance.emit("showSizeChart", {
                          sku: product.sku!,
                        })
                      }
                    }}
                  />
                </section>

                {isOutOfStock ? (
                  <div className="mt-[40px] flex items-center font-poppins font-[600] text-[#DBA802]">
                    <span className={classes.warningIcon}></span>
                    The item is currently out of stock
                  </div>
                ) : isPreOrder ? (
                  <>{note}</>
                ) : (
                  <div className="mt-[40px] flex items-center">
                    <span className={classes.shippingIcon}></span>
                    <span className="ml-[10px] font-sans text-sm leading-[1.4] text-title lg_text-[16px]">
                      Get it by:{" "}
                      <span className="font-poppins font-[600] text-primary">
                        {calculateDeliveryDate(2, 5)}
                      </span>
                    </span>
                  </div>
                )}

                <div
                  ref={ref}
                  className="mt-[20px] flex items-center justify-between gap-[10px]"
                >
                  {isOutOfStock ? null : (
                    <div className="w-[100px]">
                      <QuantitySelect showQuantity={false} form={form} />
                    </div>
                  )}
                  <div className="flex-1">
                    <AddCartButton
                      isPreOrder={isPreOrder}
                      isAddingProduct={isAddingProduct}
                      addToCartStatus={addToCartStatus}
                      label={label}
                      isOutOfStock={isOutOfStock}
                      setNotifyMeFormDialogIsOpen={setNotifyMeFormDialogIsOpen}
                      note={note}
                      isSupportedProductType={isSupportedProductType}
                    />
                  </div>
                </div>

                <div className="mt-[20px] flex items-center gap-[12px] rounded-[10px] bg-[#F7F7F5] px-[5px] py-[10px] lg_gap-[20px] lg_p-[20px]">
                  <span className={classes.daysIcon}></span>
                  <div className="flex flex-col items-start gap-[5px] lg_gap-[10px]">
                    <p className="lg_text-normal font-poppins text-sm font-[600] leading-[1.2] text-title">
                      Free Shipping & 30-Day Free Returns
                    </p>
                    <p className="font-sans text-sm text-title">
                      Shop with confidence:free shipping and risk-free 30-day
                      returns.{" "}
                    </p>
                  </div>
                </div>
                <div className="mt-[40px]">
                  <ProductDetailAndShipping
                    productDetails={productDetails}
                    product={product}
                  />
                </div>

                {
                  // 底部加车按钮
                }
                <div
                  className={cn(
                    "fixed bottom-0 left-0 z-30 w-full bg-white shadow-[0_-4px_5px_rgba(217,217,217,0.35)] transition-opacity lg_hidden",
                    inView ? "hidden opacity-0" : "block opacity-100"
                  )}
                >
                  <div className="flex justify-between gap-4 p-[15px]">
                    {isOutOfStock ? null : (
                      <div className="w-[100px]">
                        <QuantitySelect
                          className="h-[50px]"
                          showQuantity={false}
                          form={form}
                        />
                      </div>
                    )}

                    <AddCartButton
                      isPreOrder={isPreOrder}
                      isAddingProduct={isAddingProduct}
                      addToCartStatus={addToCartStatus}
                      label={label}
                      isOutOfStock={isOutOfStock}
                      setNotifyMeFormDialogIsOpen={setNotifyMeFormDialogIsOpen}
                      isSupportedProductType={isSupportedProductType}
                      note={note}
                      showReadyToShip={false}
                      className="h-[50px]"
                      outerClassName="flex-1"
                      showPreOrderTips={false}
                      showNotifyMeTips={false}
                    />
                  </div>
                </div>
              </div>

              <PCAddToCartFixed
                product={product}
                mediaGalleryEntries={mediaGalleryEntries}
                isAddingProduct={isAddingProduct}
                isOutOfStock={isOutOfStock}
                isPreOrder={isPreOrder ?? false}
                note={note}
                setNotifyMeFormDialogIsOpen={setNotifyMeFormDialogIsOpen}
                productDetails={productDetails}
                className={cn(
                  inView ? "hidden opacity-0" : "hidden opacity-100  lg_block"
                )}
                addToCartStatus={addToCartStatus}
              />
            </form>
          </Form>
        </div>
      </div>

      {
        // Titto 喂鸟器相关
        // 001CP
      }

      {product.id === 1034 ? <TTBF001CP /> : null}

      {product.id === 1035 ? <TTBF002CP /> : null}

      {
        // 002CP
        // titto-2-in-1-smart-bird-feeder.
      }

      <div id={productDetailMarkId} className="h-0 overflow-hidden" />

      {product.id === 1034 ? <TTBF001CPParams /> : null}

      {product.id === 1035 ? <TTBF002CPParams /> : null}

      <NewNotifyMeFormDialog
        isOpen={notifyMeFormDialogIsOpen}
        setNotifyMeFormDialogIsOpen={setNotifyMeFormDialogIsOpen}
        product={product}
        variants={product.variants}
        configurable_options={product.configurable_options}
        optionSelections={optionSelections}
      />

      {/* <MobileAddToCartSuccessTip
        addToCartQty={addToCartQty}
        addToCartStatus={addToCartStatus}
      /> */}
    </div>
  )
}

function PCAddToCartFixed({
  product,
  mediaGalleryEntries,
  isAddingProduct,
  isOutOfStock,
  isPreOrder,
  note,
  setNotifyMeFormDialogIsOpen,
  productDetails,
  className,
  addToCartStatus,
}: {
  product: ConfigurableProduct
  mediaGalleryEntries: any
  isAddingProduct: boolean
  isOutOfStock: boolean
  isPreOrder: boolean
  note: string
  setNotifyMeFormDialogIsOpen: any
  productDetails: any
  addToCartStatus: boolean
  className?: string
}) {
  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 z-50 hidden h-[100px] w-full bg-white shadow-[0_-2px_10px_rgba(175,175,175,0.4)] transition-all duration-lg",
        className
      )}
    >
      <div className="mx-auto h-full w-full max-w-[1440px] items-center justify-between lg_flex">
        <div className="flex items-center gap-[30px]">
          <Image
            className="h-[80px] w-[80px]"
            src={mediaGalleryEntries?.[0]?.url}
            alt={product?.name ?? ""}
            width={80}
            height={80}
          />
          <span className="line-clamp-2 max-w-[380px] overflow-hidden font-poppins text-[18px] leading-[1.4] text-title">
            {product?.name}
          </span>
          <span className="font-poppins text-[22px] font-[600] text-primary">
            <Price
              currencyCode={productDetails.price.currency}
              value={productDetails.price.value}
            />
          </span>
        </div>

        <div>
          {isOutOfStock ? (
            <span className="flex items-center font-poppins font-[600] text-[#DBA802]">
              <span className={classes.warningIcon}></span>
              The item is currently out of stock
            </span>
          ) : isPreOrder ? (
            <>{note}</>
          ) : (
            <span className="font-poppins font-[600] text-secondary">
              <span>Receive by: &nbsp;</span>
              <span>{calculateDeliveryDate(2, 5)}</span>
            </span>
          )}
        </div>

        {isOutOfStock ? (
          <>
            <Button
              type="button"
              className="h-[54px] w-[400px] max-w-full rounded-[10px] border-1 border-primary bg-white font-poppins font-[600] text-primary hover_text-white"
              onClick={() => setNotifyMeFormDialogIsOpen(true)}
            >
              Notify Me
            </Button>
          </>
        ) : (
          <Button
            className="2font-poppins h-[54px] w-[400px] rounded-[10px] bg-primary font-[600] text-white hover_opacity-90"
            disabled={isAddingProduct || addToCartStatus}
          >
            {addToCartStatus ? (
              <div className="relative flex h-full w-full items-center justify-center px-5">
                <span>ADDED</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 16 16"
                  className="absolute right-0 top-1/2 block -translate-y-1/2"
                >
                  <path
                    stroke="#fff"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 2 5.2 14 1 9.5"
                  />
                </svg>
              </div>
            ) : isAddingProduct ? (
              <AddToCartLoadingIcon />
            ) : isPreOrder ? (
              "PRE ORDER"
            ) : (
              "ADD TO CART"
            )}
          </Button>
        )}
      </div>
    </div>
  )
}
